import React from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button/Button';

import Background from 'components/Layout/Background';
import { isoDateTimeToLabel, translatePortfolioCode } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import ControlButtons from 'components/ControlButtons';

import { RootStore } from 'stores/rootStore';
import { SignatureStore } from 'stores/account/signatureStore';
import useStyles, { usePortfolioStyles } from 'components/Bucket/styles'; //, { useDevelopmentStyles }
import PortfolioTableComparison from '../../../../components/PortfolioTables/PortfolioTableComparison';

export interface Params {
  accountId: string;
}

interface Props {
  rootStore: RootStore;
  store: SignatureStore;
}

const Portfolios: React.FC<Props> = ({ rootStore, store }) => {
  const { t } = useTranslation();

  const classes = useStyles({ simple: true });
  const portfolioClasses = usePortfolioStyles();
  const currentBucket = store.buckets ? store.buckets[store.activeBucket] : undefined;
  const { portfolioComparison } = store;
  const confirmed = store.portfolios?.confirmed.find((r) => r.type === currentBucket?.type)?.time;
  const rejected = store.portfolios?.rejected.find((r) => r.type === currentBucket?.type)?.time;

  if (!store.portfolios || !store.flow) return null;

  return currentBucket ? (
    <>
      <Background>
        <Typography variant="h1">{t(store.flow[store.flowStepIndex].title)}</Typography>

        <Typography className={portfolioClasses.mainText}>{t('documents_portfolio')}</Typography>

        <Grid container item xs={12} spacing={2} className={portfolioClasses.mainText}>
          <Grid item xs={6}>
            {t('bucket_selectedInvestmentStrategy')}
            <Info
              content={t('bucket_selectedInvestmentStrategy_tooltip')}
              iconProps={{ fontSize: 'small' }}
              className={classes.tooltip}
            />
          </Grid>
          <Grid item xs={6} style={{ fontWeight: 'bold' }}>
            {translatePortfolioCode(currentBucket.portfolio.code)}
          </Grid>
          <Grid item xs={12} style={{ marginTop: '8px' }}>
            <PortfolioTableComparison
              bucketType={currentBucket.type}
              comparison={portfolioComparison[store.activeBucket]}
              showRebalancing={false}
              showInstruments={true}
              portfolioUpdate
            />
          </Grid>
          <Grid item xs={12} style={{ textAlign: 'right' }}>
            {(confirmed || rejected) && (
              <>
                {t(`portfolio_${confirmed ? 'confirmed' : 'rejected'}`)}:{' '}
                <strong style={{ fontSize: '22px' }}>{isoDateTimeToLabel(confirmed || rejected || '')}</strong>
              </>
            )}
          </Grid>
        </Grid>
      </Background>
      <ControlButtons
        isSubmitting={false}
        nextText={t(`general_confirm${store.flowStepIndex < store.flow.length - 1 ? 'AndNext' : ''}`)}
        onBack={() => store.goBack()}
        hideBack={store.flowStepIndex === 0}
        onNext={async () => store.confirmPortfolio(currentBucket.type, true)}
        lastStep={store.flow.length === 1}
        fixedBack
      >
        <Button
          variant="contained"
          type="button"
          color="default"
          onClick={async () => {
            store.confirmPortfolio(currentBucket.type, false);
          }}
        >
          {t(`general_reject${store.flowStepIndex < store.flow.length - 1 ? 'AndNext' : ''}`)}
        </Button>
      </ControlButtons>
    </>
  ) : (
    null
  );
};

export default compose(inject('rootStore'), observer)(Portfolios);
